import { useQuery } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { ReactNode } from 'react'
import { useTranslation } from '@/lib/i18n'
import { dashboardLogsQueryOptions } from '../-lib/dashboard-queries'
import { DashboardEmptyState } from './dashboard-empty-state'
import {
	DashboardQueryError,
	DashboardQueryLoading
} from './dashboard-query-state'

export function DashboardRecentLogs() {
	const logsQuery = useQuery(dashboardLogsQueryOptions())
	const { t } = useTranslation()
	let logsContent: ReactNode

	if (logsQuery.isPending) {
		logsContent = (
			<DashboardQueryLoading label={t('common:dashboard.logs_loading')} />
		)
	} else if (logsQuery.isError) {
		logsContent = (
			<DashboardQueryError
				description={t('common:dashboard.logs_error_description')}
				isRetrying={logsQuery.isFetching}
				onRetry={() => logsQuery.refetch()}
				retryLabel={t(
					logsQuery.isFetching
						? 'common:dashboard.retrying'
						: 'common:dashboard.retry'
				)}
				title={t('common:dashboard.logs_error_title')}
			/>
		)
	} else if (logsQuery.data.length === 0) {
		logsContent = (
			<div className='dashboard-logs-surface'>
				<DashboardEmptyState
					description={t('common:dashboard.no_logs_description')}
					icon='logs'
					title={t('common:dashboard.no_logs_title')}
				/>
			</div>
		)
	} else {
		logsContent = (
			<div className='table-wrap'>
				<table className='portal-table'>
					<thead>
						<tr>
							<th>{t('common:dashboard.endpoint')}</th>
							<th>{t('common:dashboard.status')}</th>
							<th>{t('common:dashboard.duration')}</th>
						</tr>
					</thead>
					<tbody>
						{logsQuery.data.map((log) => (
							<tr key={log.id}>
								<td>
									{log.method} {log.path}
								</td>
								<td>
									<span
										className={
											isSuccessful(log.statusCode)
												? 'status-pill status-pill--ok'
												: 'status-pill status-pill--warning'
										}
									>
										{log.statusCode ??
											t('common:dashboard.not_available')}
									</span>
								</td>
								<td>
									{log.durationMs === null
										? t('common:dashboard.not_available')
										: t('common:dashboard.latency_value', {
												value: log.durationMs
											})}
								</td>
							</tr>
						))}
					</tbody>
				</table>
			</div>
		)
	}

	return (
		<section className='portal-card dashboard-data-card'>
			<div className='portal-card__header'>
				<h2 className='portal-card__title'>
					{t('common:dashboard.recent_logs')}
				</h2>
				<Link
					className='portal-button portal-button--ghost portal-button--small'
					to='/logs'
				>
					{t('common:dashboard.view_all')}
				</Link>
			</div>
			{logsContent}
		</section>
	)
}

function isSuccessful(status: number | null) {
	return status !== null && status >= 200 && status < 400
}
